import React, { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { useT } from '@/contexts/I18nContext';
import { useAppState } from '../app/appContext';
import CameraSelector from './CameraSelector';
import { fetchPlanDraft, type PlanDraftResponse } from '../api/planDraft';
import { toAbsoluteUrl } from '../api/url';
import { API_HOST } from '@/api/base';
import { extractApiError } from '../utils/apiError';

// 11-stop Spectral_r LUT (matplotlib Spectral reversed). Mirrors the heatmap
// page so the auto-layout density reads with the same colour vocabulary.
const SPECTRAL_R: [number, [number, number, number]][] = [
  [0.0, [0x5e, 0x4f, 0xa2]],
  [0.1, [0x32, 0x88, 0xbd]],
  [0.2, [0x66, 0xc2, 0xa5]],
  [0.3, [0xab, 0xdd, 0xa4]],
  [0.4, [0xe6, 0xf5, 0x98]],
  [0.5, [0xff, 0xff, 0xbf]],
  [0.6, [0xfe, 0xe0, 0x8b]],
  [0.7, [0xfd, 0xae, 0x61]],
  [0.8, [0xf4, 0x6d, 0x43]],
  [0.9, [0xd5, 0x3e, 0x4f]],
  [1.0, [0x9e, 0x01, 0x42]],
];

function spectralRgb(t: number): [number, number, number] {
  const v = Math.max(0, Math.min(1, t));
  for (let i = 0; i < SPECTRAL_R.length - 1; i++) {
    const [t0, c0] = SPECTRAL_R[i];
    const [t1, c1] = SPECTRAL_R[i + 1];
    if (v <= t1) {
      const f = (v - t0) / (t1 - t0);
      return [
        Math.round(c0[0] + f * (c1[0] - c0[0])),
        Math.round(c0[1] + f * (c1[1] - c0[1])),
        Math.round(c0[2] + f * (c1[2] - c0[2])),
      ];
    }
  }
  return SPECTRAL_R[SPECTRAL_R.length - 1][1];
}

type Period = '7d' | '30d';

interface Params {
  min_score: number;
  min_box_h_px: number;
  max_box_h_px: number;
  min_aspect: number;
  min_isolation_px: number;
}

const DEFAULTS: Params = {
  min_score: 0.5,
  min_box_h_px: 60,
  max_box_h_px: 600,
  min_aspect: 1.4,
  min_isolation_px: 80,
};

function isoDateOffsetDays(days: number): { from: string; to: string } {
  const fmt = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - days);
  return { from: fmt(from), to: fmt(to) };
}

interface SliderRowProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  hint?: string;
  formatter?: (v: number) => string;
}
const SliderRow: React.FC<SliderRowProps> = ({ label, value, onChange, min, max, step, hint, formatter }) => (
  <div className="space-y-1">
    <div className="flex items-baseline justify-between">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums">{formatter ? formatter(value) : value}</span>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      className="w-full"
    />
    {hint && <div className="text-[11px] text-muted-foreground/80">{hint}</div>}
  </div>
);

const BranchPlanAutoLayout: React.FC = () => {
  const { t } = useT();
  const { camera } = useAppState();
  const cameraId = camera.selectedCameraId;

  const [period, setPeriod] = useState<Period>('7d');
  const [params, setParams] = useState<Params>(DEFAULTS);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showPoints, setShowPoints] = useState(false);

  // Debounce slider/period changes so we don't hammer the API mid-drag.
  const [debouncedParams, setDebouncedParams] = useState<Params>(params);
  useEffect(() => {
    const tid = setTimeout(() => setDebouncedParams(params), 300);
    return () => clearTimeout(tid);
  }, [params]);

  const { from, to } = useMemo(
    () => isoDateOffsetDays(period === '7d' ? 7 : 30),
    [period]
  );

  const swrKey = cameraId
    ? ['plan-draft', cameraId, from, to,
       debouncedParams.min_score, debouncedParams.min_box_h_px, debouncedParams.max_box_h_px,
       debouncedParams.min_aspect, debouncedParams.min_isolation_px]
    : null;

  const { data, error, isLoading, isValidating } = useSWR<PlanDraftResponse>(
    swrKey,
    () => fetchPlanDraft({
      camera_id: Number(cameraId),
      from, to,
      ...debouncedParams,
    }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const heatmapCanvasRef = useRef<HTMLCanvasElement>(null);
  const pointsCanvasRef = useRef<HTMLCanvasElement>(null);
  const [renderedSize, setRenderedSize] = useState<{ w: number; h: number } | null>(null);

  // Track the rendered <img> size so canvas overlays match exactly.
  // ResizeObserver handles container/responsive width changes.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const r = entries[0].contentRect;
      if (data) {
        const aspect = data.camera.image_size.h / Math.max(data.camera.image_size.w, 1);
        setRenderedSize({ w: r.width, h: r.width * aspect });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  // Draw density heatmap.
  useEffect(() => {
    const canvas = heatmapCanvasRef.current;
    if (!canvas || !data || !renderedSize) return;
    const { density } = data;
    const dpr = window.devicePixelRatio || 1;
    const W = Math.round(renderedSize.w * dpr);
    const H = Math.round(renderedSize.h * dpr);
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    if (!showHeatmap || density.max <= 0) return;

    // Render the density grid as a small ImageData, then upscale onto the
    // canvas. Smoothing softens cell edges into the familiar heatmap look.
    const small = document.createElement('canvas');
    small.width = density.cols;
    small.height = density.rows;
    const sctx = small.getContext('2d');
    if (!sctx) return;
    const img = sctx.createImageData(density.cols, density.rows);
    for (let i = 0; i < density.values.length; i++) {
      const v = density.values[i] / density.max;
      const [r, g, b] = spectralRgb(v);
      // Alpha proportional to intensity; below ~3% reads as transparent so
      // empty floor doesn't get a violet wash.
      const a = v <= 0.03 ? 0 : Math.round(60 + 165 * v);
      const o = i * 4;
      img.data[o] = r;
      img.data[o + 1] = g;
      img.data[o + 2] = b;
      img.data[o + 3] = a;
    }
    sctx.putImageData(img, 0, 0);

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(small, 0, 0, W, H);
  }, [data, renderedSize, showHeatmap]);

  // Draw clean foot-point scatter.
  useEffect(() => {
    const canvas = pointsCanvasRef.current;
    if (!canvas || !data || !renderedSize) return;
    const dpr = window.devicePixelRatio || 1;
    const W = Math.round(renderedSize.w * dpr);
    const H = Math.round(renderedSize.h * dpr);
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    if (!showPoints) return;

    const sx = renderedSize.w * dpr / data.camera.image_size.w;
    const sy = renderedSize.h * dpr / data.camera.image_size.h;
    const radius = Math.max(1.5, 2 * dpr);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
    ctx.lineWidth = 1 * dpr;
    for (const [x, y] of data.sample_points) {
      ctx.beginPath();
      ctx.arc(x * sx, y * sy, radius, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }, [data, renderedSize, showPoints]);

  const imageSrc = data?.camera.image_url
    ? toAbsoluteUrl(data.camera.image_url, API_HOST)
    : null;

  return (
    <Card>
      <CardContent className="space-y-4">
        <section>
          <div className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">
            {t('plan.autoLayoutTitle')}
          </div>
          <p className="mb-3 text-sm text-muted-foreground">
            {t('plan.autoLayoutHint')}
          </p>

          <div className="mb-3 flex flex-wrap items-center gap-3">
            <CameraSelector />
            <div className="inline-flex overflow-hidden rounded-md border border-border text-sm">
              {(['7d', '30d'] as Period[]).map(p => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  className={
                    'px-3 py-1 ' +
                    (period === p
                      ? 'bg-primary-muted text-primary-muted-foreground'
                      : 'bg-card text-muted-foreground hover:bg-muted')
                  }
                >
                  {p === '7d' ? t('plan.period.7d') : t('plan.period.30d')}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setParams(DEFAULTS)}
              className="rounded bg-muted px-3 py-1 text-sm hover:bg-muted/80"
            >
              {t('plan.resetParams')}
            </button>
            {(isLoading || isValidating) && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="size-3 animate-spin" />
                {t('plan.loading')}
              </span>
            )}
          </div>

          {!cameraId && (
            <div className="rounded-md border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
              {t('plan.selectCamera')}
            </div>
          )}

          {cameraId && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
              {/* Image + overlays */}
              <div>
                <div ref={containerRef} className="relative w-full overflow-hidden rounded-md border bg-muted/30">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt={data?.camera.name ?? ''}
                      className="block w-full select-none"
                      draggable={false}
                    />
                  ) : (
                    <div
                      className="flex w-full items-center justify-center bg-black/40 text-xs text-muted-foreground"
                      style={{ aspectRatio: data ? `${data.camera.image_size.w} / ${data.camera.image_size.h}` : '16 / 9' }}
                    >
                      {t('plan.noFrame')}
                    </div>
                  )}
                  <canvas
                    ref={heatmapCanvasRef}
                    className="pointer-events-none absolute inset-0 h-full w-full"
                  />
                  <canvas
                    ref={pointsCanvasRef}
                    className="pointer-events-none absolute inset-0 h-full w-full"
                  />
                </div>

                {/* Layer toggles */}
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm">
                  <label className="inline-flex items-center gap-2">
                    <Switch checked={showHeatmap} onCheckedChange={setShowHeatmap} />
                    <span>{t('plan.layer.heatmap')}</span>
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <Switch checked={showPoints} onCheckedChange={setShowPoints} />
                    <span>{t('plan.layer.points')}</span>
                  </label>
                  {data?.counts.truncated && (
                    <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                      {t('plan.truncated')}
                    </span>
                  )}
                </div>

                {/* Counts at each filter stage */}
                {data && (
                  <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-5">
                    <CountBox label={t('plan.count.raw')} value={data.counts.raw} />
                    <CountBox label={t('plan.count.score')} value={data.counts.after_score} />
                    <CountBox label={t('plan.count.size')} value={data.counts.after_size} />
                    <CountBox label={t('plan.count.aspect')} value={data.counts.after_aspect} />
                    <CountBox label={t('plan.count.isolation')} value={data.counts.after_isolation} highlight />
                  </div>
                )}
              </div>

              {/* Sliders panel */}
              <div className="space-y-4 rounded-md border p-3">
                <SliderRow
                  label={t('plan.param.minScore')}
                  value={params.min_score}
                  onChange={v => setParams(p => ({ ...p, min_score: v }))}
                  min={0.3} max={0.95} step={0.01}
                  formatter={v => v.toFixed(2)}
                  hint={t('plan.param.minScore.hint')}
                />
                <SliderRow
                  label={t('plan.param.minBoxH')}
                  value={params.min_box_h_px}
                  onChange={v => setParams(p => ({ ...p, min_box_h_px: v }))}
                  min={20} max={400} step={5}
                  formatter={v => `${v} px`}
                  hint={t('plan.param.minBoxH.hint')}
                />
                <SliderRow
                  label={t('plan.param.maxBoxH')}
                  value={params.max_box_h_px}
                  onChange={v => setParams(p => ({ ...p, max_box_h_px: v }))}
                  min={50} max={1200} step={10}
                  formatter={v => `${v} px`}
                  hint={t('plan.param.maxBoxH.hint')}
                />
                <SliderRow
                  label={t('plan.param.minAspect')}
                  value={params.min_aspect}
                  onChange={v => setParams(p => ({ ...p, min_aspect: v }))}
                  min={0.5} max={4.0} step={0.05}
                  formatter={v => v.toFixed(2)}
                  hint={t('plan.param.minAspect.hint')}
                />
                <SliderRow
                  label={t('plan.param.minIsolation')}
                  value={params.min_isolation_px}
                  onChange={v => setParams(p => ({ ...p, min_isolation_px: v }))}
                  min={0} max={400} step={5}
                  formatter={v => `${v} px`}
                  hint={t('plan.param.minIsolation.hint')}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="mt-3 rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {extractApiError(error, t('plan.loading'))}
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
};

interface CountBoxProps { label: string; value: number; highlight?: boolean }
const CountBox: React.FC<CountBoxProps> = ({ label, value, highlight }) => (
  <div
    className={
      'rounded-md border px-2 py-1.5 text-center ' +
      (highlight ? 'border-primary/50 bg-primary/5' : 'border-border bg-muted/30')
    }
  >
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-base font-semibold tabular-nums">{value.toLocaleString()}</div>
  </div>
);

export default BranchPlanAutoLayout;
